import { ReactIcon } from '@/components/react/ui/ReactIcon';
import { cx } from 'cva';
import type React from 'react';

interface AccordionItemProps {
  hasBorders?: boolean;
  heading: string;
  accordionName?: string;
  variant?: 'fill' | 'line' | 'comparison';
}

export const ReactAccordionItem: React.FC<
  React.PropsWithChildren<AccordionItemProps>
> = ({ heading, accordionName, hasBorders, variant, children }) => {
  // Adding variant prop to handle new comparison variant, but didn't want to break current versions that require 'hasBorders'
  const border = hasBorders || variant === 'line' || variant === 'comparison';

  return (
    <>
      <details
        {...(variant !== 'comparison' ? { name: accordionName } : {})}
        className={cx('group select-none not-open:mb-1', {
          'border-blurb-border-subtle border-t': border,
        })}
      >
        <summary
          className={cx(
            'text-blurb-text-bold flex w-full cursor-pointer items-center justify-start gap-2 py-3 transition-colors duration-300',
            border ?
              'hover:text-blurb-text-brand-bold-hover active:text-blurb-text-brand-bold-hover focus-visible:text-blurb-text-brand-bold-hover focus-visible:outline-none'
            : 'hover:bg-blurb-bg-gray-subtler-hover active:bg-blurb-bg-gray-subtler-active focus-visible:bg-blurb-bg-gray-subtler-active rounded-sm px-4',
          )}
        >
          <div
            aria-details={accordionName}
            className="flex w-full items-center justify-between gap-2"
          >
            {variant === 'comparison' ?
              <h4 className="active:text-blurb-text-link-active focus-visible:text-blurb-text-link-active text-inherit">
                {heading}
              </h4>
            : <span>{heading}</span>}
            <span className="pointer-events-none">
              <ReactIcon
                name="chevronDown"
                className="fill-blurb-text-bold ease h-6 w-6 rotate-0 transition-transform duration-500 group-open:rotate-180"
              />
            </span>
          </div>
        </summary>
      </details>
      <div id={accordionName} data-details="accordion content">
        <div
          className={cx(
            'transition-padding text-blurb-text-bold flex flex-col gap-4 py-2 text-sm',
            {
              'px-4': !hasBorders && variant !== 'comparison',
              'mb-3': hasBorders || variant === 'comparison',
              'gap-4': variant !== 'comparison',
              'gap-6': variant === 'comparison',
            },
          )}
        >
          {children}
        </div>
      </div>
    </>
  );
};
